<template>
    <b-container fluid v-if="templatesData" class="rates-page">
        <seasonality-template-templates-header ref="header" v-if="!isNil(templates)" :templates="templates" />
        <div class="mt-2" v-if="seasonsList">
            <grid
                class="rates-list-grid mb-2"
                :rowData="seasonsList"
                :columnDefs="columnDefs"
                :isSearch="false"
                :isAutoWidth="true"
            />
            <year-calendar
                v-if="renderCalendar"
                :rates="seasonsList"
                :rates-settings="templatesData.default_template_settings"
                :weekend-days="templatesData.js_weekend_days"
                :current-year="currentYear"
                :read-only="false"
                @year-changed="(value) => onChangeYear(value)"
                @day-clicked="onDayClick"
                @select-range="onSelectRange"
                :data-source="dataSource"
            />
        </div>
        <modal :id="removeSeasonModalId" title="Warning" @ok.prevent="onRemoveSeason">
            <busy-modal :show="loading" />
            Are you sure you want to delete this season?
        </modal>        
    </b-container>
    <create-edit-season-modal
        v-if="!isNil(templatesData)"
        :title="seasonModalTitle"
        :rateId="values ? values.id : 0"
        :currentYear="currentYear"
        :initValues="values"
        :maxGuests="templatesData.max_guests"
        :modalId="'create-season-modal'"
        :objects="seasonsList"
        :rateSettings="templatesData.default_template_settings"
        :ruleSettings="templatesData.default_template_settings"
        :isDefault="values ? values.default : false"
        :loading="loading"
        :editMode="editMode"
        :newObject="newSeasonObject"
        :lastObject="lastObject"
        :isSeasonality="true"
        @submit="createEditSeason"
        @reset="onReset"
        @delete="onRemoveSeasonFromModal"
    />
</template>

<script>
    import { mapGetters } from 'vuex';
    import isNil from 'lodash/isNil';
    import clone from 'lodash/clone';
    import isObject from 'lodash/isObject';    
    import API from '../../../api';
    import {
        RENTAL_PROPERTY_SETUP_RATES_ENDPOINT,
        SEASONALITY_TEMPLATE_TEMPLATE_SEASONS_LIST_ENDPOINT,
        SEASONALITY_TEMPLATE_TEMPLATE_SEASONS_DETAIL_ENDPOINT,
        SEASONALITY_TEMPLATE_TEMPLATE_LIST_ENDPOINT,
    } from '../../../endpoints';
    import ActionCellRenderer from './seasons-action-cell-renderer.vue';
    import Grid from '../../generic/grid/grid.vue';
    import Modal from '../../generic/modal/modal.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import CreateEditSeasonModal from '../../common/create-edit-rate-season-modal.vue';
    import SeasonalityTemplateTemplatesHeader from './seasonality-template-templates-header.vue';
    import format from 'date-fns/format';
    import { getSeason } from '../utils';
    import { templatesMixin } from '../mixins/templates-mixin';
    import { defineAsyncComponent } from 'vue';

    const YearCalendar = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-year-calendar" */
            '../../generic/year-calendar/year-calendar.vue'
        )
    );

    export default {
        name: 'SeasonalityTemplateTemplates',
        components: {
            Grid,
            YearCalendar,
            Modal,
            FormSelect,
            BusyModal,
            CreateEditSeasonModal,
            SeasonalityTemplateTemplatesHeader,
            actionCellRenderer: ActionCellRenderer,
        },
        mixins: [templatesMixin],
        data() {
            return {
                loading: false,
                values: null,
                editMode: false,
                renderCalendar: true,
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            seasonModalTitle() {
                if (this.values && this.values.id) {
                    return 'Edit Season';
                }
                return 'Add Season';
            },
            removeSeasonModalId() {
                return 'remove-season-' + this.values?.id;
            },            
            dataSource() {
                this.reRenderCalendar();
                return this.seasonsList.map((item) => {
                    return {
                        startDate: this.generateDate(item.period.from_date),
                        endDate: this.generateDate(item.period.to_date),
                        color: item.color,
                        rateName: item.name,
                        minNights: item.min_nights,
                        nightlyRate: item.daily_rate,
                        id: item.id,
                        default: item.default,
                    };
                });
            },
            columnDefs() {
                return [
                    {
                        field: 'name',
                        headerName: 'Name',
                        minWidth: 240,
                        autoWidthExclude: true,
                    },
                    {
                        field: 'period.display',
                        headerName: 'Period',
                        minWidth: 240,
                    },
                    {
                        field: 'min_nights',
                        headerName: 'Min Nts',
                        minWidth: 120,
                        maxWidth: 120,
                    },
                    {
                        field: 'daily_rate',
                        headerName: 'Ntly',
                    },
                    {
                        field: 'weekend_rate',
                        headerName: 'Wknd',
                        cellRenderer: ({ data }) => (data && data.weekend_rate) || 'N/A',
                    },
                    {
                        field: 'weekly_rate',
                        headerName: 'Weekly',
                        hide: this.templatesData && !this.templatesData.default_template_settings.use_weekly_rates,
                        cellRenderer: ({ data }) => {
                            return data && data.weekly_rate && data.weekly_rate > 0
                                ? data.weekly_rate
                                : this.getWeeklyRatesPlaceholder(false);
                        },
                    },
                    {
                        field: 'monthly_rate',
                        headerName: 'Monthly',
                        hide: this.templatesData && !this.templatesData.default_template_settings.use_monthly_rates,
                        cellRenderer: ({ data }) => {
                            return data && data.monthly_rate && data.monthly_rate > 0
                                ? data.monthly_rate
                                : this.getMonthlyRatesPlaceholder(false);
                        },
                    },
                    {
                        field: 'arrival_day_display',
                        headerName: 'Arrive Day	',
                        minWidth: 130,
                        hide:
                            this.templatesData &&
                            !this.templatesData.default_template_settings.arrival_day_rule_enabled,
                    },
                    {
                        field: 'departure_day_display',
                        headerName: 'Depart Day',
                        minWidth: 130,
                        hide:
                            this.templatesData &&
                            !this.templatesData.default_template_settings.departure_day_rule_enabled,
                    },
                    {
                        field: 'extra_person_base',
                        minWidth: 120,
                        maxWidth: 120,
                        headerName: 'Base Guests',
                        cellRenderer: ({ data }) => (data && data.extra_person_base) || 'N/A',
                        hide: this.templatesData && !this.templatesData.default_template_settings.use_per_person_rates,
                    },
                    {
                        field: 'extra_person_rate',
                        headerName: 'Addtl Guest Rate',
                        minWidth: 130,
                        cellRenderer: ({ data }) => (data && data.extra_person_rate) || 'N/A',
                        hide: this.templatesData && !this.templatesData.default_template_settings.use_per_person_rates,
                    },
                    {
                        field: 'delta',
                        headerName: 'Delta',
                        minWidth: 85,
                    },
                    {
                        field: '',
                        cellRenderer: 'actionCellRenderer',
                        hide: this.templatesData && this.templatesData.read_only,
                        minWidth: 175,
                        maxWidth: 175,
                        cellRendererParams: {
                            currentYear: this.currentYear,
                            rates: this.seasonsList,
                            rateSettings: this.templatesData.default_template_settings,
                            ruleSettings: this.templatesData.default_template_settings,
                            maxGuests: this.templatesData.max_guests,
                            removeSeason: async (id) => this.removeSeason(id),
                            createEditSeason: async (editMode, values) => {
                               return await this.createEditSeason(values);
                            },
                        },
                    },
                ];
            },
        },
        mounted() {
            this.fetchAllData();
            if (!isNil(this.$route.params.template)) {
                this.initialTemplate = parseInt(this.$route.params.template);
            }
        },
        methods: {
            isNil,
            getSeason,
            getIsActive(id) {
                return this.currentTab === id;
            },
            onReset() {
                this.lastObject = null;
                const period = clone(this.values.period);
                this.values = JSON.parse(JSON.stringify(this.newSeasonObject));
                this.values.period = period;
            },
            onRemoveSeasonFromModal(id) {
                this.$bvModal.hide('create-season-modal');
                this.$nextTick(() => this.$bvModal.show('remove-season-' + id));
            },
            async onRemoveSeason(id) {
                let seasonId = id;
                if (isObject(seasonId)) {
                    const modalId = id.componentId;
                    seasonId = modalId.split('-').length === 2 ? modalId.split('-')[2] : 0;
                }
                this.removeSeason(seasonId || this.values.id);
            },            
            onDayClick(value) {
                const rate = this.getSeason(this.seasonsList, value.date);
                if (rate) {
                    this.values = JSON.parse(JSON.stringify(rate));
                    this.editMode = true;
                    this.$nextTick(() => this.$bvModal.show('create-season-modal'));
                }
            },
            onSelectRange(value) {
                if (value.endDate != value.startDate) {
                    const name = `${format(value.startDate, this.userUnicodeDateFormat)} to ${format(
                        value.endDate,
                        this.userUnicodeDateFormat
                    )}`;
                    this.values = {
                        ...JSON.parse(JSON.stringify(this.newSeasonObject)),
                        name,
                        period: {
                            from_date: format(value.startDate, 'yyyy-MM-dd'),
                            to_date: format(value.endDate, 'yyyy-MM-dd'),
                        },
                    };
                    this.editMode = false;
                    this.$nextTick(() => this.$bvModal.show('create-season-modal'));
                }
            },
            async removeSeason(id) {
                try {
                    this.busy = true;
                    await API.delete(SEASONALITY_TEMPLATE_TEMPLATE_SEASONS_DETAIL_ENDPOINT(id));
                    await this.fetchTemplates();
                    await this.fetchSeasonsList();
                    this.$bvModal.hide('remove-season-' + id);
                    this.$bvModal.hide('create-season-modal');
                    this.successToast({
                        text: 'Season successfuly deleted.',
                    });
                    this.reRenderCalendar();
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not delete rate.',
                    });
                } finally {
                    this.busy = false;
                }
            },
            generateDate(dateString) {
                if (dateString) {
                    const datesArr = dateString.split('-');
                    return new Date(datesArr[0], datesArr[1] - 1, datesArr[2]);
                }
                return null;
            },
            onChangeYear(value) {
                if (value !== this.currentYear) {
                    this.currentYear = value;
                    this.fetchSeasonsList();
                }
            },
            async createEditSeason(values) {
                try {
                    this.loading = true;
                    const payload = {
                        ...values,
                        from_date: values.period.from_date,
                        to_date: values.period.to_date,
                        period: values.default
                            ? null
                            : {
                                  from_date: values.period.from_date,
                                  to_date: values.period.to_date,
                              },
                        template: this.selectedTemplate.id,
                    };

                    payload.weekend_rate = payload.weekend_rate_enabled ? payload.weekend_rate : null;
                    payload.weekly_rate = payload.weekly_rate_enabled ? payload.weekly_rate : null;
                    payload.monthly_rate = payload.monthly_rate_enabled ? payload.monthly_rate : null;
                    payload.arrival_day = payload.arrival_day_enabled ? payload.arrival_day : null;
                    payload.departure_day = payload.departure_day_enabled ? payload.departure_day : null;
                    payload.extra_person_rate = payload.extra_person_rate_enabled ? payload.extra_person_rate : null;
                    payload.extra_person_base = payload.extra_person_rate_enabled ? payload.extra_person_base : 1;

                    var response = null;

                    if (values.id == 0) {
                        response = await API.post(SEASONALITY_TEMPLATE_TEMPLATE_SEASONS_LIST_ENDPOINT(), payload);
                    } else {
                        response = await API.put(
                            SEASONALITY_TEMPLATE_TEMPLATE_SEASONS_DETAIL_ENDPOINT(values.id),
                            payload
                        );
                    }

                    this.lastObject = response.data;

                    await this.fetchTemplates(true);
                    await this.fetchSeasonsList();

                    this.reRenderCalendar();
                    this.$bvModal.hide('create-season-modal');
                } catch (e) {
                    this.formattedErrorToast(e, 'Could not update season');
                } finally {
                    this.loading = false;
                }
            },
            reRenderCalendar() {
                this.renderCalendar = false;
                this.$nextTick(() => {
                    this.renderCalendar = true;
                });
            },
            async fetchAllData() {
                await this.fetchTemplatesData();
                await this.fetchTemplates(true);
                await this.fetchPropertyTemplateSettings();
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    .rates-page {
        padding-right: 15px;
    }

    .rates-list-grid {
        height: 250px !important;
    }
</style>
